import type { ProgressSourceRef } from "./types";

const REF_TYPES = new Set<ProgressSourceRef["type"]>([
  "commit",
  "pull_request",
  "deployment",
  "release",
  "workflow",
  "link",
]);

export function parseProgressSourceRefs(value: unknown): {
  refs: ProgressSourceRef[];
  error: string | null;
} {
  if (!Array.isArray(value)) return { refs: [], error: "source_refs must be an array" };
  if (value.length > 100) return { refs: [], error: "source_refs supports at most 100 items" };

  const refs: ProgressSourceRef[] = [];
  for (const [index, candidate] of value.entries()) {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      return { refs: [], error: `source_refs[${index}] must be an object` };
    }
    const row = candidate as Record<string, unknown>;
    if (typeof row.type !== "string" || !REF_TYPES.has(row.type as ProgressSourceRef["type"])) {
      return { refs: [], error: `source_refs[${index}].type is invalid` };
    }
    if (typeof row.label !== "string" || !row.label.trim() || row.label.length > 300) {
      return { refs: [], error: `source_refs[${index}].label must be 1-300 characters` };
    }
    if (typeof row.url !== "string" || !safeHttpUrl(row.url)) {
      return { refs: [], error: `source_refs[${index}].url must be an http(s) URL` };
    }
    if (
      row.sha !== undefined &&
      (typeof row.sha !== "string" || row.sha.length > 200)
    ) {
      return { refs: [], error: `source_refs[${index}].sha must be at most 200 characters` };
    }
    if (
      row.status !== undefined &&
      (typeof row.status !== "string" || row.status.length > 200)
    ) {
      return { refs: [], error: `source_refs[${index}].status must be at most 200 characters` };
    }
    refs.push({
      type: row.type as ProgressSourceRef["type"],
      label: row.label.trim(),
      url: row.url,
      ...(typeof row.sha === "string" && row.sha.trim() ? { sha: row.sha.trim() } : {}),
      ...(typeof row.status === "string" && row.status.trim()
        ? { status: row.status.trim() }
        : {}),
    });
  }
  return { refs, error: null };
}

function safeHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return value.length <= 2_000 && (url.protocol === "https:" || url.protocol === "http:");
  } catch {
    return false;
  }
}
